import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  IndianRupee, TrendingUp, TrendingDown, AlertCircle, CheckCircle2,
  Search, RefreshCw, FileText, Users, Truck, Filter, ArrowRight,
} from "lucide-react";
import { KpiCard, StatusBadge } from "@/components/erp/primitives";
import TableExportButton from "@/components/TableExportButton";

const fmtINR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthStartISO = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const todayISO = () => new Date().toISOString().slice(0, 10);
const yearStartISO = () => {
  // Indian FY starts April 1st
  const d = new Date();
  const y = d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear();
  return `${y}-04-01`;
};

const OVERDUE_DAYS = 60;

export default function AccountsDashboard() {
  const nav = useNavigate();
  const [companyFilter, setCompanyFilter] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [fromDate, setFromDate] = useState(yearStartISO());
  const [toDate, setToDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState(null);

  const [supplierDues, setSupplierDues] = useState([]);
  const [supplierQ, setSupplierQ] = useState("");
  const [supplierOverdueOnly, setSupplierOverdueOnly] = useState(false);

  const [customerRcv, setCustomerRcv] = useState([]);
  const [customerQ, setCustomerQ] = useState("");
  const [customerOverdueOnly, setCustomerOverdueOnly] = useState(false);

  useEffect(() => {
    api.get("/dashboard/companies").then((r) => setCompanyOptions(r.data || [])).catch(() => {});
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (companyFilter) params.company = companyFilter;
      const [k, s, c] = await Promise.all([
        api.get(`/accounts-dashboard/kpis`, { params }),
        api.get(`/accounts-dashboard/supplier-dues`, {
          params: { ...params, overdue_only: supplierOverdueOnly, q: supplierQ },
        }),
        api.get(`/accounts-dashboard/customer-receivables`, {
          params: { ...params, overdue_only: customerOverdueOnly, q: customerQ },
        }),
      ]);
      setKpis(k.data);
      setSupplierDues(s.data || []);
      setCustomerRcv(c.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [companyFilter, fromDate, toDate, supplierOverdueOnly, supplierQ, customerOverdueOnly, customerQ]);

  const payable = kpis?.payable || {};
  const receivable = kpis?.receivable || {};

  const scrollTo = (id, setOverdue) => {
    if (typeof setOverdue === "function") setOverdue(true);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  return (
    <div className="space-y-6" data-testid="accounts-dashboard">
      {/* HEADER */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Module · Accounts</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <IndianRupee size={22} /> Accounts Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Consolidated Accounts Payable &amp; Receivable · overdue threshold: <span className="font-mono font-semibold">{OVERDUE_DAYS} days</span>.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Company</label>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              data-testid="ad-company"
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white min-w-[160px]"
            >
              <option value="">All Companies</option>
              {companyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              data-testid="ad-from" className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              data-testid="ad-to" className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" />
          </div>
          <button
            type="button"
            onClick={() => { setFromDate(yearStartISO()); setToDate(todayISO()); }}
            data-testid="ad-fy"
            className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md"
            title="Set From = 1-Apr of current FY"
          >
            This FY
          </button>
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            data-testid="ad-refresh"
            className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* ACCOUNTS PAYABLE — 4 KPIs */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">Accounts Payable</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button onClick={() => scrollTo("supplier-dues-section", setSupplierOverdueOnly.bind(null, false))} className="text-left">
            <KpiCard testid="kpi-total-purchase" title="Total Purchase Value" value={fmtINR(payable.total_purchase_value)} icon={FileText} variant="blue" subtitle="Bills booked in range" />
          </button>
          <button onClick={() => scrollTo("supplier-dues-section", setSupplierOverdueOnly.bind(null, false))} className="text-left">
            <KpiCard testid="kpi-total-paid" title="Total Paid Value" value={fmtINR(payable.total_paid_value)} icon={CheckCircle2} variant="emerald" subtitle="Cleared to suppliers" />
          </button>
          <button onClick={() => scrollTo("supplier-dues-section", () => setSupplierOverdueOnly(false))} className="text-left">
            <KpiCard testid="kpi-balance-payable" title="Balance Payable" value={fmtINR(payable.balance_payable)} icon={TrendingDown} variant="amber" subtitle="Purchase − Paid" />
          </button>
          <button onClick={() => scrollTo("supplier-dues-section", () => setSupplierOverdueOnly(true))} className="text-left">
            <KpiCard testid="kpi-due-payable" title="This Month Due / Overdue" value={fmtINR(payable.this_month_due_payable)} icon={AlertCircle} variant="rose" subtitle={`> ${OVERDUE_DAYS} days · click to filter`} />
          </button>
        </div>
      </div>

      {/* ACCOUNTS RECEIVABLE — 4 KPIs */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">Accounts Receivable</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button onClick={() => scrollTo("customer-rcv-section", () => setCustomerOverdueOnly(false))} className="text-left">
            <KpiCard testid="kpi-total-sales" title="Total Sales Value" value={fmtINR(receivable.total_sales_value)} icon={FileText} variant="blue" subtitle="Invoices raised in range" />
          </button>
          <button onClick={() => scrollTo("customer-rcv-section", () => setCustomerOverdueOnly(false))} className="text-left">
            <KpiCard testid="kpi-received" title="Payment Received" value={fmtINR(receivable.payment_received_value)} icon={CheckCircle2} variant="emerald" subtitle="Collected from customers" />
          </button>
          <button onClick={() => scrollTo("customer-rcv-section", () => setCustomerOverdueOnly(false))} className="text-left">
            <KpiCard testid="kpi-balance-rcv" title="Balance Receivable" value={fmtINR(receivable.balance_receivable)} icon={TrendingDown} variant="amber" subtitle="Sales − Received" />
          </button>
          <button onClick={() => scrollTo("customer-rcv-section", () => setCustomerOverdueOnly(true))} className="text-left">
            <KpiCard testid="kpi-due-rcv" title="This Month Due / Overdue" value={fmtINR(receivable.this_month_due_receivable)} icon={AlertCircle} variant="rose" subtitle={`> ${OVERDUE_DAYS} days · click to filter`} />
          </button>
        </div>
      </div>

      {/* SUPPLIER DUE PAYMENT LIST */}
      <div id="supplier-dues-section" className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2"><Truck size={15} /> Supplier Due Payment List <span className="text-slate-400 font-normal">({supplierDues.length})</span></div>
            <div className="text-xs text-slate-500 mt-0.5">All bills with balance &gt; 0 · click supplier name for the full ledger.</div>
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-1.5 cursor-pointer">
              <input type="checkbox" checked={supplierOverdueOnly} onChange={(e) => setSupplierOverdueOnly(e.target.checked)} data-testid="supplier-overdue-only" />
              Overdue only
            </label>
            <div className="inline-flex items-center gap-2 bg-slate-100 rounded-md px-3 py-1.5 text-xs">
              <Search size={12} className="text-slate-500" />
              <input value={supplierQ} onChange={(e) => setSupplierQ(e.target.value)}
                placeholder="Supplier / Bill No…"
                data-testid="supplier-search"
                className="bg-transparent outline-none text-sm min-w-[180px]" />
            </div>
            <TableExportButton
              testIdPrefix="supplier-dues-export"
              filename="supplier_due_payments"
              rows={supplierDues}
              columns={[
                { key: "supplier_name", label: "Supplier" },
                { key: "supplier_bill_number", label: "Bill No" },
                { key: "grn_number", label: "GRN Number" },
                { key: "grn_date", label: "GRN Date" },
                { key: "bill_date", label: "Bill Date" },
                { key: "bill_amount", label: "Bill Amount" },
                { key: "paid_amount", label: "Paid Amount" },
                { key: "balance_amount", label: "Balance" },
                { key: "days_since_bill", label: "Days" },
                { key: "status", label: "Status" },
              ]}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <SupplierDuesTable rows={supplierDues} onSupplierClick={(name) => nav(`/accounts-dashboard/supplier/${encodeURIComponent(name)}`)} />
        </div>
      </div>

      {/* CUSTOMER RECEIVABLE LIST */}
      <div id="customer-rcv-section" className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2"><Users size={15} /> Customer Receivable List <span className="text-slate-400 font-normal">({customerRcv.length})</span></div>
            <div className="text-xs text-slate-500 mt-0.5">All invoices with balance &gt; 0 · click customer name for the full ledger.</div>
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-1.5 cursor-pointer">
              <input type="checkbox" checked={customerOverdueOnly} onChange={(e) => setCustomerOverdueOnly(e.target.checked)} data-testid="customer-overdue-only" />
              Overdue only
            </label>
            <div className="inline-flex items-center gap-2 bg-slate-100 rounded-md px-3 py-1.5 text-xs">
              <Search size={12} className="text-slate-500" />
              <input value={customerQ} onChange={(e) => setCustomerQ(e.target.value)}
                placeholder="Customer / Invoice No…"
                data-testid="customer-search"
                className="bg-transparent outline-none text-sm min-w-[180px]" />
            </div>
            <TableExportButton
              testIdPrefix="customer-rcv-export"
              filename="customer_receivables"
              rows={customerRcv}
              columns={[
                { key: "customer_name", label: "Customer" },
                { key: "invoice_number", label: "Invoice No" },
                { key: "invoice_date", label: "Invoice Date" },
                { key: "invoice_value", label: "Invoice Value" },
                { key: "payment_received", label: "Received" },
                { key: "balance_amount", label: "Balance" },
                { key: "days_since_invoice", label: "Days" },
                { key: "status", label: "Status" },
              ]}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <CustomerRcvTable rows={customerRcv} onCustomerClick={(name) => nav(`/accounts-dashboard/customer/${encodeURIComponent(name)}`)} />
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Reusable table components
// -----------------------------------------------------------------------------
function StatusPill({ status, days, overdueDays = 60 }) {
  const k = String(status || "").toLowerCase();
  if (k === "paid") return <StatusBadge status="paid" label="Paid" />;
  if (days >= overdueDays) return <StatusBadge status="rejected" label={`Overdue`} />;
  if (k === "partially paid") return <StatusBadge status="partial" label="Partial" />;
  return <StatusBadge status="pending" label="Pending" />;
}

function SupplierDuesTable({ rows, onSupplierClick }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
          <th className="text-left px-4 py-3">Supplier</th>
          <th className="text-left px-4 py-3">Bill No</th>
          <th className="text-left px-4 py-3">GRN No</th>
          <th className="text-left px-4 py-3">GRN Date</th>
          <th className="text-left px-4 py-3">Bill Date</th>
          <th className="text-right px-4 py-3">Bill Amt</th>
          <th className="text-right px-4 py-3">Paid</th>
          <th className="text-right px-4 py-3">Balance</th>
          <th className="text-right px-4 py-3">Days</th>
          <th className="text-left px-4 py-3">Status</th>
        </tr>
      </thead>
      <tbody data-testid="supplier-dues-body">
        {rows.length === 0 && (
          <tr><td colSpan="10" className="text-center py-14 text-slate-400 text-sm">No pending supplier bills for the current filter</td></tr>
        )}
        {rows.map((r) => (
          <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50 ${r.is_overdue ? "bg-rose-50/40" : ""}`}>
            <td className="px-4 py-3">
              <button
                onClick={() => onSupplierClick(r.supplier_name)}
                className="text-[#1D2951] hover:underline font-medium inline-flex items-center gap-1"
                data-testid={`supplier-link-${r.id}`}
              >
                {r.supplier_name} <ArrowRight size={12} className="opacity-50" />
              </button>
            </td>
            <td className="px-4 py-3 font-mono text-xs">{r.supplier_bill_number || "—"}</td>
            <td className="px-4 py-3 font-mono text-xs">{r.grn_number || "—"}</td>
            <td className="px-4 py-3 font-mono text-xs">{r.grn_date || "—"}</td>
            <td className="px-4 py-3 font-mono text-xs">{r.bill_date}</td>
            <td className="px-4 py-3 text-right font-mono">{fmtINR(r.bill_amount)}</td>
            <td className="px-4 py-3 text-right font-mono text-emerald-700">{fmtINR(r.paid_amount)}</td>
            <td className="px-4 py-3 text-right font-mono text-amber-700 font-semibold">{fmtINR(r.balance_amount)}</td>
            <td className={`px-4 py-3 text-right font-mono ${r.is_overdue ? "text-rose-700 font-semibold" : "text-slate-600"}`}>{r.days_since_bill}</td>
            <td className="px-4 py-3"><StatusPill status={r.status} days={r.days_since_bill} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CustomerRcvTable({ rows, onCustomerClick }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
          <th className="text-left px-4 py-3">Customer</th>
          <th className="text-left px-4 py-3">Invoice No</th>
          <th className="text-left px-4 py-3">Invoice Date</th>
          <th className="text-right px-4 py-3">Invoice Value</th>
          <th className="text-right px-4 py-3">Received</th>
          <th className="text-right px-4 py-3">Balance</th>
          <th className="text-right px-4 py-3">Days</th>
          <th className="text-left px-4 py-3">Status</th>
        </tr>
      </thead>
      <tbody data-testid="customer-rcv-body">
        {rows.length === 0 && (
          <tr><td colSpan="8" className="text-center py-14 text-slate-400 text-sm">No outstanding customer invoices for the current filter</td></tr>
        )}
        {rows.map((r) => (
          <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50 ${r.is_overdue ? "bg-rose-50/40" : ""}`}>
            <td className="px-4 py-3">
              <button
                onClick={() => onCustomerClick(r.customer_name)}
                className="text-[#1D2951] hover:underline font-medium inline-flex items-center gap-1"
                data-testid={`customer-link-${r.id}`}
              >
                {r.customer_name} <ArrowRight size={12} className="opacity-50" />
              </button>
            </td>
            <td className="px-4 py-3 font-mono text-xs">{r.invoice_number || "—"}</td>
            <td className="px-4 py-3 font-mono text-xs">{r.invoice_date}</td>
            <td className="px-4 py-3 text-right font-mono">{fmtINR(r.invoice_value)}</td>
            <td className="px-4 py-3 text-right font-mono text-emerald-700">{fmtINR(r.payment_received)}</td>
            <td className="px-4 py-3 text-right font-mono text-amber-700 font-semibold">{fmtINR(r.balance_amount)}</td>
            <td className={`px-4 py-3 text-right font-mono ${r.is_overdue ? "text-rose-700 font-semibold" : "text-slate-600"}`}>{r.days_since_invoice}</td>
            <td className="px-4 py-3"><StatusPill status={r.status} days={r.days_since_invoice} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
